// Leveranser (admin mockup §5): expected receipts with their progress while open and the
// closing record afterwards — every difference and the reason the operator gave.

import { useEffect, useState } from "react";
import { Admin, Api, type ExpectedReceiptDetail, type ExpectedReceiptSummary, type ReceiptProgress, type Qty } from "../api/client.js";
import { fmtNum, fmtQty, t } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";
import { selectedSiteId } from "./Sites.jsx";
import { Returns } from "./Returns.jsx";

const fmt = (iso: string) => new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });

function sumSame(received: Qty[], uom: string): number {
  return received.filter((q) => q.uom === uom).reduce((a, q) => a + Number(q.value), 0);
}

export function Deliveries() {
  const [filter, setFilter] = useState<"open" | "closed">("open");
  // Purchases and customer returns (owner 2026-11-04: returns get their own tab here).
  const [tab, setTab] = useState<"purchase" | "return">("purchase");
  const [rows, setRows] = useState<ExpectedReceiptSummary[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<ExpectedReceiptDetail | null>(null);
  useDetailHistory(!!(detail || selected), () => { setSelected(null); setDetail(null); });
  const [progress, setProgress] = useState<ReceiptProgress | null>(null);

  useEffect(() => {
    if (tab === "return") return;
    Admin.receipts(filter === "open" ? "open,proposed" : "closed,cancelled", selectedSiteId() ?? "all", "purchase").then((r) => {
      setRows(r);
      // The list stays a list until a row is chosen (owner 2026-11-01: the detail opens as its own page).
    }).catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, tab]);

  useEffect(() => {
    if (!selected) return;
    Api.getReceipt(selected).then(setDetail).catch(() => setDetail(null));
    Api.progress(selected).then(setProgress).catch(() => setProgress(null));
  }, [selected]);

  const statusPill = (s: string) => <span className={`pill ${s === "closed" ? "ok" : s === "open" ? "warn" : "mut"}`}>{t(`adm.del.status.${s}` as "adm.del.status.open")}</span>;
  const pct = (r: ExpectedReceiptSummary) => (r.id === selected && progress && detail ? Math.min(100, Math.round((100 * progress.lines.reduce((a, l) => a + sumSame(l.received, l.expected.uom), 0)) / Math.max(1, progress.lines.reduce((a, l) => a + Number(l.expected.value), 0)))) : null);

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.deliveries")}</h1><div className="crumb">{rows ? `${rows.length}` : ""}</div></div>
        <div className="right">
          <span className="seg"><button type="button" className={filter === "open" ? "on" : ""} onClick={() => { setFilter("open"); setSelected(null); }}>{t("adm.del.open")}</button><button type="button" className={filter === "closed" ? "on" : ""} onClick={() => { setFilter("closed"); setSelected(null); }}>{t("adm.del.closed")}</button></span>
        </div>
      </div>
      <div className="adm-content">
        {!(detail || selected) && (
          <div className="adm-tabs">
            <button type="button" className={tab === "purchase" ? "on" : ""} onClick={() => setTab("purchase")}>{t("adm.ret.tabPurchase")}</button>
            <button type="button" className={tab === "return" ? "on" : ""} onClick={() => setTab("return")}>{t("adm.ret.tabReturn")}</button>
          </div>
        )}
        {tab === "return" ? <Returns filter={filter} /> : <div className={`adm-cols${!!(detail || selected) ? " detail-open" : ""}`}>
          <div className="card adm-grow">
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>{t("adm.del.order")}</th><th>{t("adm.del.supplier")}</th><th>{t("adm.del.expected")}</th><th className="num">{t("adm.del.lines")}</th><th>{t("adm.del.received")}</th><th>{t("adm.col.status")}</th></tr></thead>
                <tbody>
                  {rows?.length === 0 && <tr><td colSpan={6} className="mut">—</td></tr>}
                  {rows?.map((r) => (
                    <tr key={r.id} className={`click${r.id === selected ? " sel" : ""}`} onClick={() => setSelected(r.id)}>
                      <td className="id">{r.reference}</td>
                      <td>{r.supplier?.name ?? "—"}</td>
                      <td className="id">{r.expectedAt ?? "—"}</td>
                      <td className="num">{r.lines}{r.unresolvedLines > 0 ? <span className="mut"> · {t("pick.unmapped", { n: r.unresolvedLines })}</span> : null}</td>
                      <td>{pct(r) !== null ? <div className="bar" style={{ height: 8, borderRadius: 999, background: "var(--panel-2)", overflow: "hidden" }}><i style={{ display: "block", height: "100%", width: `${pct(r)}%`, background: "var(--accent)" }} /></div> : null}</td>
                      <td>{statusPill(r.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="adm-w460 adm-detail adm-detail-only" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {!!(detail || selected) && <DetailBar title={detail ? detail.reference : t("common.loading")} sub={undefined} onBack={() => { setSelected(null); setDetail(null); }} />}
            {detail && (
              <>
                <div className="card">
                  <div className="hd">{detail.reference} · {detail.supplier?.name ?? ""} <span className="sub">{progress ? `${progress.pallets} ${t("adm.del.pallets").toLowerCase()} · ${progress.containers} kolli` : ""}</span></div>
                  <div className="bd flush">
                    <table className="at">
                      <thead><tr><th>{t("adm.del.lines")}</th><th className="num">{t("adm.del.ordered")}</th><th className="num">{t("adm.del.received")}</th><th className="num">{t("adm.del.diff")}</th></tr></thead>
                      <tbody>
                        {detail.lineItems.map((l) => {
                          const p = progress?.lines.find((x) => x.lineNo === l.lineNo);
                          const got = p ? sumSame(p.received, l.uom) : 0;
                          const diff = got - Number(l.quantity);
                          return <tr key={l.lineNo}><td>{l.lineNo}. {l.productName ?? l.description ?? l.sku}</td><td className="num">{fmtQty(l.quantity, l.uom)}</td><td className="num">{fmtQty(got, l.uom)}</td><td className="num" style={{ color: diff === 0 ? "var(--ok)" : "var(--warn)" }}>{diff === 0 ? "✓" : `${diff > 0 ? "+" : ""}${fmtNum(diff)}`}</td></tr>;
                        })}
                        {progress?.extra.map((x, i) => <tr key={`x${i}`}><td>{x.productName ?? `GTIN ${x.gtin ?? "?"}`} <span className="mut">· {t("rows.notOnOrder")}</span></td><td className="num">—</td><td className="num">{x.received.map((q) => fmtQty(q.value, q.uom)).join(" + ")}</td><td className="num" style={{ color: "var(--warn)" }}>extra</td></tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="card">
                  <div className="hd">{t("adm.del.pallets")}</div>
                  <div className="bd flush">
                    <table className="at">
                      <thead><tr><th>SSCC</th><th className="num">Kolli</th><th>{t("adm.st.location")}</th><th>{t("adm.col.who")}</th></tr></thead>
                      <tbody>
                        {progress?.palletList.length === 0 && <tr><td colSpan={4} className="mut">—</td></tr>}
                        {progress?.palletList.map((p) => <tr key={p.containerId}><td className="id">{p.sscc ?? p.containerId}</td><td className="num">{p.children}</td><td className="id">{p.locationCode ?? "—"}</td><td className="mut">{p.actorId.replace(/^user:/, "")} {fmt(p.at)}</td></tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="card">
                  <div className="hd">{t("adm.del.closing")} <span className="sub">{detail.closedAt ? t("adm.del.closedBy", { at: fmt(detail.closedAt), by: (detail.closing?.actorId ?? "").replace(/^user:/, "") }) : ""}</span></div>
                  <div className="bd">
                    {!detail.closing && <p className="ahint">{t("adm.del.closingNone")}</p>}
                    {detail.closing && (detail.closing.differences?.length ?? 0) === 0 && <p className="ahint">{t("adm.del.noDiff")}</p>}
                    {detail.closing && detail.closing.differences && detail.closing.differences.length > 0 && (
                      <table className="at">
                        <thead><tr><th>{t("adm.del.lines")}</th><th className="num">{t("adm.del.ordered")}</th><th className="num">{t("adm.del.received")}</th><th>{t("adm.del.reason")}</th></tr></thead>
                        <tbody>
                          {detail.closing.differences.map((d, i) => <tr key={i}><td>{d.lineNo !== null ? `${d.lineNo}. ${detail.lineItems.find((l) => l.lineNo === d.lineNo)?.productName ?? ""}` : t("rows.notOnOrder")}</td><td className="num">{d.expected ? fmtQty(d.expected.value, d.expected.uom) : "—"}</td><td className="num">{d.received.map((q) => fmtQty(q.value, q.uom)).join(" + ") || "0"}</td><td>{d.reason}</td></tr>)}
                        </tbody>
                      </table>
                    )}
                    {detail.closing?.note && <p className="ahint" style={{ marginTop: 8 }}>{t("adm.del.note")}: {detail.closing.note}</p>}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>}
      </div>
    </>
  );
}
